<template>
  <div class="base-split-button">
    <button type="button" class="base-split-button__button" @click="$emit('click')">
      <slot />
    </button>
    <span v-if="$slots.list" :id="`base-split-button-${_uid}-actions-button`" class="base-split-button__actions-button" aria-haspopup="true" aria-expanded="listShow ? 'true' : 'false'" :aria-controls="`base-split-button-${_uid}-list`" tabindex="-1" title="Open for more actions" @click="onButtonClick($event)" @keydown.down="onButtonDown()" @keydown.up="onButtonUp()">
      <icon name="chevron-right" style="transform: rotate(90deg);" />
      <span class="visually-hidden">Vis muligheder</span>
    </span>
    <div v-if="$slots.list" class="base-split-button__list" :id="`base-split-button-${_uid}-list`" :class="{ 'base-split-button__list--hidden': !listShow }" :aria-labelledby="`base-split-button-${_uid}-button`" @keydown.down="onListDown()" @keydown.up="onListUp()" @keydown.esc="onListEscape()">
      <slot name="list" />
    </div>
  </div>
</template>

<script>
export default {
    name: 'BaseSplitButton',
    data () {
        return {
            listShow: false,
            itemIndex: -1
        }
    },
    computed: {
        interactiveElements () {
            return this.$el.querySelectorAll('.base-split-button__list [role="menuitem"]')
        }
    },
    mounted () {
        if (!this.$slots.hasOwnProperty('default') || (this.$slots.hasOwnProperty('default') && this.$slots.default.some(property => property.elm && property.elm.nodeName === '#text' && property.text.trim() === ''))) {
            throw new Error('Text is missing. Set the Text by using the default slot.')
        } else if (this.$slots.hasOwnProperty('default') && this.$slots.default.some(property => property.elm && property.elm.nodeName === 'IMG' && property.elm.attributes.alt.value.trim() === '')) {
            throw new Error('Text is missing. Seems like you are using an image, so make sure to set the alt attribute.')
        } else if (this.$slots.hasOwnProperty('default') && this.$slots.default.some(property => !property.elm && property.text.trim() === '')) {
            throw new Error('Text is missing. Seems like you are trying to hack the component. `null` and empty string are not allowed.')
        }

        document.addEventListener('click', this.clickOutside)
    },
    destroyed () {
        document.removeEventListener('click', this.clickOutside)
    },
    methods: {
        clickOutside (event) {
            if (this.$el.contains(event.target)) {
                return
            }
            this.listShow = false
            this.itemIndex = -1
        },

        listOpen () {
            this.listShow = true
            this.itemIndex = 0
            this.setActive()
        },
        listClose () {
            this.listShow = false
            this.itemIndex = -1
            this.$el.querySelector('.base-split-button__button').focus()
        },

        setActive () {
            if (this.interactiveElements.length > 0 && this.interactiveElements[this.itemIndex]) {
                this.$nextTick(() => {
                    this.interactiveElements[this.itemIndex].focus()
                })
            }
        },

        onButtonClick () {
            if (this.$slots.list) {
                if (!this.listShow) {
                    this.listOpen()
                } else {
                    this.listClose()
                }
            }
        },
        onButtonDown () {
            event.preventDefault()
            this.listOpen()
        },
        onButtonUp () {
            event.preventDefault()
            this.listClose()
        },
        onListDown () {
            event.preventDefault()
            const focusedElement = document.activeElement
            const currentIndex = Array.from(this.interactiveElements).indexOf(focusedElement)

            if (currentIndex !== -1) {
                this.itemIndex = currentIndex
            }

            this.itemIndex++
            if (this.itemIndex > this.interactiveElements.length - 1) {
                this.itemIndex = this.interactiveElements.length - 1
            }
            this.setActive()
        },
        onListUp () {
            event.preventDefault()
            const focusedElement = document.activeElement
            const currentIndex = Array.from(this.interactiveElements).indexOf(focusedElement)

            if (currentIndex !== -1) {
                this.itemIndex = currentIndex
            }

            this.itemIndex--
            if (this.itemIndex < 0) {
                this.itemIndex = 0
            }
            this.setActive()
        },
        onListEscape () {
            this.listClose()
        }
    }
}
</script>

<style lang="scss">
.base-split-button {
    background: #fff;
    border-radius: 3px;
    color: #424242;
    display: inline-flex;
    min-width: 96px;
    position: relative;
    text-transform: uppercase;
    transition: background-color 0.3s ease, color 0.3s ease;

    &__button {
        background: none;
        border: none;
        border-radius: 3px;
        box-shadow: none;
        font-size: 0.8rem;
        font-weight: 500;
        line-height: 1.25em;
        min-width: 36px;
        padding: 1.5em 1em;
        text-transform: uppercase;
        width: 100%;

        &:hover,
        &:focus-visible {
            background-color: #e5eff2;
        }
    }
    &:has(&__actions-button) &__button {
        border-radius: 3px 0 0 3px;
    }
    &__actions-button {
        border-left: 1px solid rgba(66, 66, 66, 0.6);
        border-radius: 0 3px 3px 0;
        cursor: pointer;
        padding: 15px 8px;

        &:hover,
        &:focus-visible {
            background-color: #e5eff2;
        }

        svg {
            fill: #333;
            height: 16px;
            width: 16px;
        }
    }
    &__list {
        background: #fff;
        border: 1px solid currentColor;
        border-radius: 3px;
        box-shadow:
            0.3px 0.3px 0.7px rgba(0, 0, 0, 0.02),
            0.8px 0.8px 1.6px rgba(0, 0, 0, 0.028),
            1.5px 1.5px 3px rgba(0, 0, 0, 0.035),
            2.7px 2.7px 5.4px rgba(0, 0, 0, 0.042),
            5px 5px 10px rgba(0, 0, 0, 0.05),
            12px 12px 24px rgba(0, 0, 0, 0.07);
        min-width: 180px;
        opacity: 0;
        padding: 4px 0;
        pointer-events: none;
        position: absolute;
        right: 0;
        top: calc(100% + 4px);
        transform: translateY(-12px);
        transition: opacity 0.2s var(--ease-in-out-circ), transform 0.3s var(--ease-in-out-circ), visibility 0s 0.3s var(--ease-in-out-circ);
        z-index: 12;

        &:focus-within {
            opacity: 1;
            pointer-events: auto;
            transform: none;
            transition: opacity 0.2s 0.1s var(--ease-in-out-circ), transform 0.3s var(--ease-in-out-circ);
        }

        ul {
            list-style: none;
            margin-bottom: 0;
            margin-top: 0;
            padding-left: 0;

            li {
                font-size: 1.28rem;
                font-weight: 500;
                line-height: 1.25em;

                & + li {
                    border-top: 1px solid rgba(66, 66, 66, 0.4);
                }

                button {
                    font-size: 0.8rem;
                    font-weight: 500;
                    line-height: 1.25em;
                    min-width: 36px;
                    padding: 1em;
                    text-align: left;
                    text-transform: uppercase;
                    width: 100%;

                    &:hover,
                    &:focus-visible {
                        background-color: #e5eff2;
                    }
                }
            }
        }
    }
}
</style>



// WEBPACK FOOTER //
// src/components/base/BaseSplitButton.vue